import React, { useState, useEffect } from 'react';
import { Table, Form, Button, Modal, Card } from "react-bootstrap";
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEye, faPen, faPlus, faTrash, faFilter, faSync 
} from '@fortawesome/free-solid-svg-icons';
import useFetch from "../../../hooks/useFetch";
import RequireRoles from '../../../components/RequireRoles';
import Swal from "sweetalert2";
import PaginatedItems from '../../../components/administracao/Pagination';

export default function GerenciarCategorias() {
    // https://create-react-app.dev/docs/adding-custom-environment-variables/
    const { params } = useParams(); // pega o que vem depois da barra na url
    const { get, del } = useFetch();

    const defaultState = {
        categorias: [],
        count: 0,
        page: 1,
        filtroAtivo: false,
        filtros: {
            nome_cat: "",
            descricao: ""
        },
        mostrarModal: false,
        registroAExcluir: { nome_cat: "" },
        loading: true
    };

    // Roda quando a página é carregada https://reactjs.org/docs/hooks-reference.html#lazy-initial-state
    const [state, setState] = useState(() => {
        return defaultState;
    });

    useEffect(() => {
        async function initialize() {
            const [categorias, count] = await buscarCategorias({});
            setState({ ...state, categorias: categorias, count: count, loading: false });
        }
        initialize();
    }, [params]);

    // função de paginação
    async function handlePageChange(pageNumber) {
        window.scrollTo(0, 0);
        let filtroPaginacao = { page: pageNumber };
        if (state.filtroAtivo) {
            filtroPaginacao = { ...filtroPaginacao, page: pageNumber };
        }
        let [categorias, count] = await buscarCategorias(filtroPaginacao);
        setState({ ...state, page: pageNumber, categorias: categorias, count: count, loading: false });
    }

    // Busca os dados do banco
    async function buscarCategorias(filtros) {
        if (!state.loading) setState({ ...state, loading: true });
        // filtros = { nome: "nome_cat", descricao: "descricao" }
        let query = "?";
        if (filtros && Object.keys(filtros).length > 0) {
            for (const key in filtros) {
                if (filtros[key] !== "") {
                    query = query.concat(key, "=", filtros[key], "&");
                }
            }
        }
        // no fim fica: nome_cat=nome_cat&descricao=descricao&
        try {
            let response = await get('/categoria' + query);
            if (response.ok) {
                let dados = response.result;
                return [dados.data, dados.count];
            } else {
                console.error('Erro no buscarCategorias()');
                return [];
            }
        } catch (error) {
            console.error('Erro no buscarCategorias():', error);
        }
    }

    // Função acionada quando o usuário clica em buscar
    async function filtrarCategorias() {
        // Busca os dados do banco com os filtros
        let [categorias, count] = await buscarCategorias(state.filtros);
        // Atualiza o estado com os dados buscados
        setState({ ...state, categorias: categorias, count: count, filtroAtivo: true, loading: false });
    }

    async function limparFiltros() {
        // Busca os dados do banco sem filtros
        let [categorias, count] = await buscarCategorias({});
        // Atualiza o estado com os dados buscados e reseta os valores dos campos de filtro
        setState({ ...state, filtros: defaultState.filtros, count: count, filtroAtivo: false, categorias: categorias, loading: false });
    }

    function handleChangeFiltros(event) {
        let target = event.target;
        let value = target.value;
        let name = target.name;

        // se for dropdown, pega o valor selecionado
        if (target.type === 'select-one') {
            value = target.options[target.selectedIndex].value;
        }

        setState({
            ...state,
            filtros: {
                ...state.filtros,
                [name]: value
            }
        });
    }

    async function excluirCategoria() {
        let categoria = state.registroAExcluir;
        try {
            let response = await del('/categoria/' + categoria.id_categoria);
            if (response.status === 200) {
                let categorias = await buscarCategorias({});
                setState({ ...state, categorias: categorias, mostrarModal: false, registroAExcluir: {}, loading: false });
            }
            else if (response.status === 300) {
                fecharModal();
                Swal.fire({
                    title: "Não foi possível excluir a categoria",
                    text: "A categoria está sendo usada por um item.",
                    icon: "error",
                })
            }
            else {
                console.error('Erro no excluirCategoria()');
            }
        } catch (error) {
            console.error('Erro no excluirCategoria():', error);
        }
    }

    function fecharModal() {
        setState({ ...state, mostrarModal: false, registroAExcluir: {} });
    }

    return (
        <div className="admin-page-container">
            <div className="admin-header-container">
                <div className="title-group">
                    <h2 className="admin-title">Gerenciamento de Categorias</h2>
                    <p className="admin-subtitle">Organize as classificações técnicas do acervo</p>
                </div>
                <Link to="/admin/categorias/criar" className="btn-add-new">
                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Categoria
                </Link>
            </div>

            <Card className="filter-card-admin mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={(e) => { e.preventDefault(); filtrarCategorias(); }}>
                        <div className="row g-3 align-items-end">
                            <Form.Group className="col-md-8">
                                <Form.Label className="label-admin">Nome da Categoria</Form.Label>
                                <Form.Control
                                    className="input-moderno"
                                    placeholder="Ex: Mobiliário, Instrumento..."
                                    name="nome_cat"
                                    value={state.filtros.nome_cat}
                                    onChange={handleChangeFiltros}
                                />
                            </Form.Group>
                            <div className="col-md-4 d-flex gap-2">
                                <Button type="button" className="btn-apply-filters w-100" onClick={filtrarCategorias}>
                                    <FontAwesomeIcon icon={faFilter} className="me-2" /> Filtrar
                                </Button>
                                <Button type="button" className="btn-clear-filters" onClick={limparFiltros} title="Limpar">
                                    <FontAwesomeIcon icon={faSync} />
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="table-card-admin">
                <div className="table-responsive">
                    <Table className="modern-table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>#</th>
                                <th>Categoria</th>
                                <th>Descrição</th>
                                <th className="text-center">Opções</th>
                            </tr>
                        </thead>
                        <tbody>
                            {state.loading ? (
                                <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-teal"></div></td></tr>
                            ) : state.categorias.length > 0 ? (
                                state.categorias.map((item, index) => (
                                    <tr key={index}>
                                        <td data-label="#" className="text-muted small">#{item.id_categoria}</td>
                                        <td data-label="Categoria" className="fw-bold">{item.nome_cat}</td>
                                        <td data-label="Descrição" className="text-muted small">{item.descricao || '-'}</td>
                                        <td data-label="Opções">
                                            <div className="d-flex justify-content-center gap-2">
                                                <Link className="action-icon view" to={`/admin/categorias/${item.id_categoria}`} state={{ acao: "" }}><FontAwesomeIcon icon={faEye} /></Link>
                                                <Link className="action-icon edit" to={`/admin/categorias/${item.id_categoria}`} state={{ acao: "Editar" }}><FontAwesomeIcon icon={faPen} /></Link>
                                                <RequireRoles allowedRoles={['Desenvolvedor', 'Administrador']}>
                                                    <button className="action-icon delete" onClick={() => setState({ ...state, mostrarModal: true, registroAExcluir: item })}><FontAwesomeIcon icon={faTrash} /></button>
                                                </RequireRoles>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="4" className="text-center py-4">Nenhuma categoria encontrada.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                {state.categorias.length > 0 && (
                    <div className="card-footer bg-white border-0 py-3 d-flex justify-content-center">
                        <PaginatedItems itemCount={state.count} itemsPerPage={20} handlePageChange={handlePageChange} />
                    </div>
                )}
            </Card>

            <Modal show={state.mostrarModal} onHide={() => setState({ ...state, mostrarModal: false })} centered className="modern-modal">
                <Modal.Header closeButton className="modal-header-teal">
                    <Modal.Title className="text-white fs-5">Excluir Categoria</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <p className="mb-0">Tem certeza que deseja excluir a categoria <strong>{state.registroAExcluir.nome_cat}</strong>?</p>
                </Modal.Body>
                <Modal.Footer className="border-0 p-4 pt-0">
                    <Button variant="light" className="btn-modal-cancel" onClick={() => setState({ ...state, mostrarModal: false })}>Cancelar</Button>
                    <Button variant="danger" className="px-4" onClick={excluirCategoria}>Confirmar Exclusão</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}